import React, { ChangeEvent } from 'react'

import { useLanguage } from 'language/translator'

import TextField from 'components_new/atoms/TextField'

interface NumberFieldProps {
  // Defaults to false. Set true to allow a leading minus sign (negatives).
  allowNegative?: boolean
  // Defaults to true to preserve existing behaviour. Set false when rendering
  // many fields (e.g. a table) so they don't fight over focus on mount.
  autoFocus?: boolean
  label?: string
  onChange: (
    value: string // if 'decimal' returns value with a '.'
  ) => void
  precision?: number
  size?: 'small' | 'medium'
  sx?: object
  type?: 'number' | 'decimal'
  value: string
}

const restrictInput = (
  value: string,
  type: 'number' | 'decimal',
  precision?: number,
  allowNegative?: boolean
) => {
  // Keep a single leading minus sign when negatives are allowed.
  const sign = allowNegative && value.trim().startsWith('-') ? '-' : ''

  if (type === 'number') {
    // Only allow digits for 'number' type
    return sign + value.replace(/[^0-9]/g, '')
  }

  if (type === 'decimal') {
    // Allow numbers and a single decimal separator
    const parsedValue = value
      .replace(/[^0-9,.]/g, '') // Remove any non-numeric and non-separator characters
      .replace(/\,/g, '.') // Replace , with .
      .replace(/[.](?=.*[.])/g, '') // Prevent multiple .

    if (precision !== undefined && parsedValue.includes('.')) {
      const [integerPart, decimalPart] = parsedValue.split('.')

      // Restrict the decimal part to the specified precision
      const restrictedDecimalPart = decimalPart.slice(0, precision)

      return `${sign}${integerPart}.${restrictedDecimalPart}`
    }

    return sign + parsedValue
  }

  return value
}

const NumberField = (props: NumberFieldProps) => {
  const {
    allowNegative = false,
    autoFocus = true,
    label,
    onChange,
    precision,
    size = 'medium',
    type = 'number',
    value
  } = props

  const { lang } = useLanguage()

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = restrictInput(e.target.value, type, precision, allowNegative)

    onChange(value)
  }

  const displayValue =
    lang === 'sv' && type === 'decimal' ? value.replace('.', ',') : value

  return (
    <TextField
      autoFocus={autoFocus}
      label={label}
      onChange={handleChange}
      size={size}
      type={'text'}
      value={displayValue}
    />
  )
}

export default NumberField
